// StreamingLoader
// Drop-in replacement for ./Loader.js. Constructor signature unchanged:
//   new StreamingLoader(viewer, glbs, ReactAPI, isIfcProject)
//
// Streaming mode activates when `glbs.manifestUrl` is provided. Falls
// back to legacy eager Loader otherwise.
//
// Public surface preserved (matching legacy Loader):
//   viewer.meshesObject     Map<uniqueId, {mesh, index, fragments[], ...}>
//   viewer.meshes           InstancedMesh[]
//   viewer.loadedModels     Group push
//   ReactAPI.setLoading / setError / setPercentsLoaded / setAllowToResize
//   modelProps              still exposed
//
// What's new (additive):
//   viewer.streaming.{setShowOutliers, toggleShowOutliers, ...}
//   viewer.streaming.state  — ViewerStateStore with hide/show/isolate/highlight
//   viewer.streaming.applicator — InstanceStateApplicator
//   this.streamingController.tick()  hooked into viewer.animate
//   this.dispose()
//   window.__sl                       debug handle (browser console only)
//
// === STATE STORE WIRING ===
//
// Construct order matters:
//   1. ViewerStateStore (no deps)
//   2. InstanceStateApplicator (depends on store)
//   3. MapMeshesAdapter (depends on applicator + store, for promotion)
//   4. StreamingController (depends on adapter + applicator)
//   5. applicator.bindToStore(adapter) AFTER both are alive — so the
//      store's emit on hide/show/highlight reaches the applicator
//      which can find slots via the adapter.
//
// === FRAME ARCHITECTURE ===
//
// As of the current C# pipeline, the exporter writes per-instance
// matrices and tile bounding boxes already in three.js Y-up frame.
// No JS-side rotation is required to bring them into viewer frame.
//
// IFC projects are an EXCEPTION: legacy IFC files were authored under
// a different convention and the JS loader has historically applied a
// (π, π, π) rotation to flip them. That behavior is preserved here.
//
// Practically:
//   - non-IFC: root group has identity rotation. rootMatrix is the
//     identity matrix. All downstream applyMatrix4 calls reduce to
//     no-ops. Cheap; we keep them in place rather than special-casing
//     because the cost is negligible and the code stays uniform with
//     the IFC path.
//   - IFC: root group has (π, π, π) rotation. rootMatrix carries that
//     transform; tile bboxes / trimmed bounds / full bounds get
//     rotated through it the same way they always did.
//
// Why keep `root` as a Group at all when non-IFC needs no rotation?
// Two reasons: (1) it's still useful as a parent we can walk for
// scene cleanup on dispose, and (2) `viewer.loadedModels.push(root)`
// is part of the legacy contract that downstream code relies on
// (e.g. clipping helpers iterate loadedModels). Keeping the group
// preserves that contract with zero overhead.
//
// === MATRIX CAPTURE ===
//
// We still snapshot rootMatrix after explicit
// scene.updateMatrixWorld so the snapshot doesn't accidentally read
// identity from a not-yet-walked Group. For non-IFC the snapshot is
// just identity, but the code path doesn't care.
//
// === PREFETCH POLICY === (unchanged)
//
// sidecarPrefetchRadius=null. Animate-tick handles all sidecar/fragment
// loading.

import { sceneBumps } from './renderSignal';
import drawCensus from './drawCensus';
import { Box3, Matrix4, Raycaster, Vector2, Vector3, Euler, Group } from 'three';
import { forgetNestedParts } from './nestedParts';
import { activate as activateSourceFilter, projectIdFromManifestUrl } from './sourceFilterRegistry';
import { activate as activateDocumentTransforms, isActive as documentTransformsActive } from './documentTransformRegistry';

import GeometryCache from './GeometryCache';
import GLBFetcher from './GLBFetcher';
import GLTFWorkerPool from './GLTFWorkerPool';
import IDBBlobCache from './IDBBlobCache';
import InstancePoolRegistry from './instancePoolRegistry';
import LODSelector from './LODSelector';
import { computeLodBands } from './lodBands';
import { packingEnabled } from './packingFlags';
import { wantsCacheClear, clearStreamingCache } from './cacheFlags';
import { modelsHostStats, applyTilesBase, resetStreamFlags } from './streamBase';
import fetchLedger from './fetchLedger';
import thrashLedger from './thrashLedger';
import { packsEnabled } from './packFormat';
import FrustumTester from './FrustumTester';
import TileRegistry from './TileRegistry';
import MapMeshesAdapter from './mapMeshesAdapter';
import StreamingController from './streamingController';
import ManifestLoader from './ManifestLoader';
import OutlierVisualizer from './OutlierVisualizer';

import ViewerStateStore from './ViewerStateStore';
import InstanceStateApplicator from './InstanceStateApplicator';

import LegacyLoader from '..';
import { isClearanceHash } from './clearanceFilter';
import frameProfile from './frameProfile';
import { fullBoundsAreStray } from './strayBounds';

/**
 * LOD switch distances for this model, in feet, as
 * [maxFine, maxMedium, Infinity].
 *
 * Scaled to the scene diagonal rather than fixed, so the same rule
 * behaves the same way on a cottage and on a tower: at one diagonal the
 * whole model fills the view, so full detail is wanted well past that,
 * and "far enough not to matter" is a multiple of the model's own size.
 *
 * The instance count pulls the fine band in on heavy models. Fine is
 * roughly 6-7x the triangles of medium per fragment, and holding every
 * tile at fine is affordable at a few hundred thousand triangles but not
 * at tens of millions. Thresholds are deliberately generous: a typical
 * building lands in the top branch.
 */

export default class StreamingLoader {
  // One-call performance picture for a loaded model, readable from the
  // console or a headless run as window.__sl.perfSnapshot():
  //   draw calls / triangles per frame (three's renderer.info), frame
  //   interval and render time over the last 120 frames, tile states,
  //   pool count and instance slots, geometry cache size, JS heap.
  // Tells whether a slow model is draw-call bound, triangle bound or
  // main-thread bound before anyone changes the exporter or the viewer.
  // === BATCH DIAGNOSTICS ===
  //
  // Both of these exist because a batched item that will not select is
  // indistinguishable, from the outside, from a click that landed on
  // air: a tile batch spans a whole tile, so the middle of its bounding
  // sphere is usually empty space. batchProbe says what the batches are;
  // pickProbe aims a ray at one real item and reports both what the ray
  // hit and the pixel to click to reproduce it.

  batchProbe(limit = 5) {
    const v = this.viewer;
    const inScene = [];
    if (v && v.scene) v.scene.traverse((o) => { if (o.isBatchedMesh) inScene.push(o); });
    const raycastSet = Array.isArray(v && v.meshes) ? v.meshes : [];
    const rows = inScene.slice(0, limit).map((mesh) => {
      const material = mesh.material || {};
      const tint = material.userData && material.userData.__batchTint;
      const first = mesh.userData && mesh.userData[0];
      const pos = new Vector3();
      const snap = mesh.userData && mesh.userData.snapshotMatrices;
      if (snap) pos.set(snap[12], snap[13], snap[14]);
      return {
        name: mesh.name,
        items: mesh.batchItemCount || 0,
        visible: !!mesh.visible,
        inRaycastSet: raycastSet.indexOf(mesh) >= 0,
        perObjectFrustumCulled: !!mesh.perObjectFrustumCulled,
        // The tint texture lives under this exact key; a probe looking
        // for "tintTexture" would report it missing when it is there.
        tintTexture: tint ? `${tint.image.width}x${tint.image.height}` : null,
        transparent: !!material.transparent,
        firstUid: first ? first.uniqueId : null,
        firstVisibleAt: mesh.getVisibleAt ? mesh.getVisibleAt(0) : null,
        firstWorldPos: [+pos.x.toFixed(1), +pos.y.toFixed(1), +pos.z.toFixed(1)],
      };
    });
    return {
      batchesInScene: inScene.length,
      batchesInRaycastSet: inScene.filter((m) => raycastSet.indexOf(m) >= 0).length,
      raycastSetSize: raycastSet.length,
      batching: this.streamingController ? this.streamingController.batching : null,
      batchedTiles: this.streamingController ? this.streamingController._batchedTiles : null,
      batchedItems: this.streamingController ? this.streamingController._batchedItems : null,
      rows,
    };
  }

  pickProbe(uid) {
    const v = this.viewer;
    if (!v || !v.camera) return { error: 'no viewer' };
    const rec = v.meshesObject && v.meshesObject.get(uid);
    if (!rec) return { error: 'no record for uid', uid };
    const mesh = rec.mesh || (rec.fragments && rec.fragments[0] && rec.fragments[0].mesh);
    const index = rec.index !== undefined ? rec.index
      : (rec.fragments && rec.fragments[0] ? rec.fragments[0].index : -1);
    if (!mesh || index < 0) return { error: 'record has no slot', uid };

    // A point that is actually ON the item, not the origin of its
    // transform: the centre of its own bounding box in world space.
    const matrix = new Matrix4();
    const box = new Box3();
    const centre = new Vector3();
    const isBatch = !!mesh.isBatchedMesh;
    const snap = mesh.userData && mesh.userData.snapshotMatrices;
    if (snap) matrix.fromArray(snap, index * 16);
    if (isBatch && mesh.getBoundingBoxAt) {
      mesh.getBoundingBoxAt(index, box);
    } else if (mesh.geometry) {
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      if (mesh.geometry.boundingBox) box.copy(mesh.geometry.boundingBox);
    }
    if (!box.isEmpty()) { box.getCenter(centre); centre.applyMatrix4(matrix); }
    else { centre.setFromMatrixPosition(matrix); }

    // Where that point sits on screen, so the same hit can be clicked.
    const ndc = centre.clone().project(v.camera);
    const el = v.renderer && v.renderer.domElement;
    const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : { left: 0, top: 0, width: 0, height: 0 };
    const clientX = rect.left + ((ndc.x + 1) / 2) * rect.width;
    const clientY = rect.top + ((1 - ndc.y) / 2) * rect.height;

    // The same intersect the click handler runs.
    const raycaster = new Raycaster();
    raycaster.setFromCamera(new Vector2(ndc.x, ndc.y), v.camera);
    const set = (Array.isArray(v.meshes) ? v.meshes : []).filter((m) => m.visible);
    const hits = raycaster.intersectObjects(set).slice(0, 4).map((hit) => {
      const data = hit.object && hit.object.userData;
      // Resolved the way the CLICK handler resolves it, not a second
      // implementation: a probe that agrees with itself proves nothing.
      const id = (v && typeof v.resolvePickIndex === 'function')
        ? v.resolvePickIndex(hit)
        : (hit.instanceId !== undefined ? hit.instanceId : hit.batchId);
      return {
        object: hit.object && hit.object.name,
        type: hit.object && hit.object.type,
        instanceId: hit.instanceId,
        batchId: hit.batchId,
        pickIndex: id,
        resolvedUid: (id !== undefined && data && data[id]) ? data[id].uniqueId : null,
        distance: +hit.distance.toFixed(2),
      };
    });
    return {
      uid,
      inBatch: isBatch,
      meshName: mesh.name,
      slot: index,
      visibleAt: isBatch && mesh.getVisibleAt ? mesh.getVisibleAt(index) : undefined,
      worldPoint: [+centre.x.toFixed(1), +centre.y.toFixed(1), +centre.z.toFixed(1)],
      onScreen: ndc.z > -1 && ndc.z < 1 && Math.abs(ndc.x) <= 1 && Math.abs(ndc.y) <= 1,
      clickAt: [Math.round(clientX), Math.round(clientY)],
      raycastSetSize: set.length,
      hits,
      resolvesThisUid: hits.some((h) => h.resolvedUid === uid),
    };
  }

  // Console counterpart of `?cache=clear`: window.__sl.clearCache().
  // CLEARANCE ZONES. The switch lives on the controller; this is the handle
  // the pages call, because a page should not have to know that streaming
  // has a controller inside it.
  //
  // Returns how many tiles were re-materialised, so a caller can tell the
  // difference between "nothing carried clearance" and "the call did not
  // reach the controller" - zero from a working call and zero from a broken
  // one look identical otherwise.
  setClearanceVisible(visible) {
    const c = this.streamingController;
    if (!c || typeof c.setClearanceVisible !== 'function') return null;   // null, not 0
    return c.setClearanceVisible(visible);
  }

  clearanceVisible() {
    const c = this.streamingController;
    return !!(c && typeof c.clearanceVisible === 'function' && c.clearanceVisible());
  }

  // What the toggle can see, counted from the loaded rows. Null when there is
  // no controller to ask - a caller must be able to tell "nothing carries
  // clearance" from "I could not find out".
  clearanceCoverage() {
    const c = this.streamingController;
    if (!c || typeof c.clearanceCoverage !== 'function') return null;
    return c.clearanceCoverage();
  }

  // Requests per URL since this model started loading (fetchLedger).
  fetchesByUrl() {
    return fetchLedger.byUrl();
  }

  clearCache() {
    return clearStreamingCache(this._blobCache);
  }

  // window.__sl.thrash(): every tile's loads / unloads / reloads / hides and
  // unload reasons, and the reloaded fragment hashes with their counts
  // (thrashLedger.js). perfSnapshot().thrash is the summary of the same.
  thrash(limit = 500) {
    return thrashLedger.detail(limit);
  }

  // Where a frame's time goes, for `seconds`: window.__sl.profile(10).
  // Off until asked; see frameProfile.js.
  profile(seconds = 10) {
    return frameProfile(this, seconds);
  }

  perfSnapshot() {
    const v = this.viewer || {};
    const r = v.renderer;
    const info = r && r.info ? r.info : { render: {}, memory: {} };
    const fs = v.__frameStats || { render: [], interval: [] };
    const avg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
    const p95 = (a) => { if (!a.length) return 0; const s = [...a].sort((x, y) => x - y); return s[Math.min(s.length - 1, Math.floor(s.length * 0.95))]; };
    // Tile states are TileRegistry.TileState values: idle,
    // sidecar_loading, fragments_loading, visible, unloaded.
    const tiles = { total: 0, idle: 0, sidecar_loading: 0, fragments_loading: 0, visible: 0, unloaded: 0, other: 0 };
    let sidecarRows = 0;
    const reg = this.streamingController && this.streamingController.registry;
    if (reg && typeof reg.forEach === 'function') {
      reg.forEach((t) => {
        tiles.total += 1;
        const st = String(t.state || 'other').toLowerCase();
        if (st in tiles && st !== 'total') tiles[st] += 1; else tiles.other += 1;
        if (t.instancesByHash) for (const arr of t.instancesByHash.values()) sidecarRows += arr.length;
      });
    }
    // Pools: one InstancedMesh per fragment hash (per LOD slot); the
    // registry's own stats() is the source of truth when present.
    let pools = null;
    try {
      const pr = this.streamingController && this.streamingController.poolRegistry;
      if (pr && typeof pr.stats === 'function') { const st = pr.stats(); pools = { count: st.poolCount, slots: st.totalCount, capacity: st.totalCapacity, free: st.totalFree }; }
      else if (pr && pr.pools) { let count = 0; let slots = 0; pr.pools.forEach((arr) => (Array.isArray(arr) ? arr : [arr]).forEach((p) => { if (p) { count += 1; slots += p.count || 0; } })); pools = { count, slots }; }
    } catch (e) { pools = null; }
    const mem = (typeof performance !== 'undefined' && performance.memory) ? performance.memory : null;
    return {
      frame: {
        intervalMsAvg: +avg(fs.interval).toFixed(2), intervalMsP95: +p95(fs.interval).toFixed(2),
        renderMsAvg: +avg(fs.render).toFixed(2), renderMsP95: +p95(fs.render).toFixed(2),
        fpsAvg: fs.interval.length ? +(1000 / avg(fs.interval)).toFixed(1) : 0,
        samples: fs.interval.length,
      },
      // Per-frame GPU counters accumulated over the last full frame
      // (viewer.animate resets renderer.info once per frame and copies it
      // after the composer draws); the raw renderer.info only shows the
      // last pass.
      gpu: { drawCalls: fs.gpu ? fs.gpu.calls : info.render.calls, triangles: fs.gpu ? fs.gpu.triangles : info.render.triangles,
             lines: fs.gpu ? fs.gpu.lines : info.render.lines, points: fs.gpu ? fs.gpu.points : info.render.points,
             geometries: info.memory.geometries, textures: info.memory.textures, programs: (info.programs || []).length,
             directRenderSkipped: !!(v.postProcessing && v.postProcessing.composer) && v.skipDirectRenderWithComposer !== false },
      tiles, sidecarRows,
      // The guarantee on what the camera is pointing at: how many tiles
      // it covers and how many of them still owe fragments.
      focus: this.streamingController && typeof this.streamingController.focusStatus === 'function'
        ? this.streamingController.focusStatus() : null,
      // The first-seconds burst: whether it is still open and which
      // budgets are in force right now.
      coldStart: this.streamingController && typeof this.streamingController.coldStartStatus === 'function'
        ? this.streamingController.coldStartStatus() : null,
      // Requests answered by models.bidlight.com vs sent back to /stream.
      modelsHost: modelsHostStats(),
      // Every model-byte request this load sent (fetchLedger): requests per
      // URL, duplicates (a URL asked again after it was answered), bytes,
      // in-flight peak. A cold load should show duplicates near 0.
      // window.__sl.fetchesByUrl() lists every URL with its request count.
      fetches: fetchLedger.summary(),
      // Evict/reload churn (thrashLedger): library evictions by cap, libraries
      // reloaded after eviction (top 20 hashes), per-tile unload/reload with
      // reasons (top 20 tiles), and 5 s buckets of evictions, reloads, tile
      // loads and unloads - a loop shows as a plateau that does not drain.
      // window.__sl.thrash() has the full per-tile and per-hash detail.
      thrash: thrashLedger.summary(),
      idb: this._blobCache && typeof this._blobCache.stats === 'function' ? this._blobCache.stats() : null,
      // Fragments served from packs vs loaded one by one (manifest `fragmentPacks`).
      packs: this._fetcher && this._fetcher.packHomes
        ? { indexed: this._fetcher.packHomes.size, ...this._fetcher._packStats } : null,
      pools,
      // WHO the last frame's calls and triangles went to (drawCensus.js):
      // pools (with their hole and parked triangles split out), tile
      // batches by role, ghost twins, merges and the rest, plus the
      // visible pools by live instance count. gpu above is the total;
      // this is the breakdown, and passFactor says whether the GPU walked
      // the scene more than once.
      drawn: (() => {
        try {
          return drawCensus({
            scene: v.scene,
            poolRegistry: this.streamingController && this.streamingController.poolRegistry,
            gpu: fs.gpu || null,
          });
        } catch (e) { return { error: String(e && e.message) }; }
      })(),
      // Where the triangles come from: per fragment hash, triangles of
      // the pool's current-LOD geometry × occupied slots (includes the
      // zero-scale duplicates, which the GPU still processes). Top 15
      // plus the total, so "triangle bound" can be blamed on heavy
      // fragments or on the long tail.
      // Who bumped the scene version (each bump = a frame drawn with the
      // camera still), and what render-on-demand did with it.
      sceneBumps: sceneBumps(),
      renderOnDemand: (this.viewer && this.viewer._rod) ? { drawn: this.viewer._rod.drawn, skipped: this.viewer._rod.skipped, sceneWakes: this.viewer._rod.sceneWakes || 0 } : null,
      triangleBudget: (() => {
        try {
          const pr = this.streamingController && this.streamingController.poolRegistry;
          if (!pr || !pr.pools) return null;
          const rows = []; let total = 0; let slots = 0;
          // What the GPU is actually asked to draw: an InstancedMesh draws
          // mesh.count instances, INCLUDING released holes below the tail
          // and parked/duplicate slots (zero-scaled, still vertex work).
          // drawnTris vs totalTris says how much of the frame is holes.
          let drawnTris = 0; let drawnSlots = 0; let holeSlots = 0;
          pr.pools.forEach((arr, hash) => {
            (Array.isArray(arr) ? arr : [arr]).forEach((p, lod) => {
              if (!p || !p.mesh || !p.mesh.geometry) return;
              const st = typeof p.stats === 'function' ? p.stats() : { count: p.count || 0, free: 0 };
              const active = Math.max(0, (st.count || 0) - (st.free || 0));
              if (!active) return;
              const g = p.mesh.geometry;
              const tris = g.index ? g.index.count / 3 : (g.attributes.position ? g.attributes.position.count / 3 : 0);
              const t = Math.round(tris * active);
              total += t; slots += active;
              if (p.mesh.visible !== false) {
                const n = p.mesh.count || 0;
                drawnSlots += n; drawnTris += Math.round(tris * n);
                holeSlots += Math.max(0, n - active);
              }
              rows.push({ hash: String(hash).slice(0, 60), lod, instances: active, trisEach: Math.round(tris), tris: t, visible: p.mesh.visible !== false });
            });
          });
          rows.sort((a, b) => b.tris - a.tris);
          // Tile batches (BatchedMesh, one draw call each): triangles of all
          // their items, so poolsDrawn + batchTris is what the GPU is sent.
          let batchTris = 0; let batchMeshes = 0;
          const reg = this.streamingController && this.streamingController.registry;
          if (reg) reg.forEach((t) => {
            const b = t && t._batch;
            if (!b || !Array.isArray(b.meshes)) return;
            for (const m of b.meshes) {
              if (!m || m.visible === false || !m.geometry) continue;
              batchMeshes++;
              const g = m.geometry;
              batchTris += g.index ? Math.round(g.index.count / 3) : Math.round(((g.attributes.position && g.attributes.position.count) || 0) / 3);
            }
          });
          return { totalTris: total, activeSlots: slots, drawnTris, drawnSlots, holeSlots, batchTris, batchMeshes, sentTris: drawnTris + batchTris, top: rows.slice(0, 15) };
        } catch (e) { return null; }
      })(),
      deferral: this.streamingController ? {
        fragmentsIndexed: this.fragmentsIndex && this.fragmentsIndex.fragments ? Object.keys(this.fragmentsIndex.fragments).length : 0,
        heavy: (this.heavyFragments || []).length,
        deferredHashes: this.streamingController._deferredHashCount,
        revivedHashes: this.streamingController._revivedHashCount,
        retirePasses: this.streamingController._retiredPasses || 0,
        // Rest-pass health (2026-10-13): a forced retire swap that refused
        // itself (and why, last hash), the passes skipped because nothing
        // changed, and pool-hole compaction.
        // What the passes PLAN to draw: the floor's budget plus the cone's
        // admitted extra (triangles and new calls). Compare the GPU to this.
        plannedTris: (this.streamingController._budgetTris || 0) + ((this.streamingController._coneStats && this.streamingController._coneStats.triangles) || 0),
        plannedCalls: (this.streamingController._budgetCalls || 0) + ((this.streamingController._coneStats && this.streamingController._coneStats.calls) || 0),
        stale: (() => { try { return this.streamingController.staleStats(); } catch (e) { return null; } })(),
        swapsRefused: this.streamingController._swapsRefused || 0,
        swapRefusedLast: this.streamingController._swapRefusedLast || null,
        budgetSkips: this.streamingController._budgetSkips || 0,
        restSkips: this.streamingController._restSkips || 0,
        layoutSeq: this.streamingController._layoutSeq || 0,
        compactions: this.streamingController._compactions || 0,
        compactedSlots: this.streamingController._compactedSlots || 0,
        // Tiles sent to a coarser level while the camera was moving
        // (streamingController `coarsenWhileMoving`).
        coarsenSwaps: this.streamingController._coarsenSwaps || 0,
        // Loading tiles drawn early (streamingController `progressiveFlushMs`).
        progressiveFlushes: this.streamingController._progressiveFlushes || 0,
        progressiveChunks: this.streamingController._progressiveChunks || 0,
        quality: this.streamingController.quality,
        drawEverything: !!this.streamingController.drawEverything,
        lodFinePx: this.streamingController.lodFinePx,
        lodMediumPx: this.streamingController.lodMediumPx,
        materializeBudgetMs: this.streamingController.materializeBudgetMs,
        budgetYields: this.streamingController._budgetYields,
        poolsDisposed: this.streamingController._poolsDisposed,
        trimmedSlots: (this.streamingController.poolRegistry && this.streamingController.poolRegistry.trimmedSlots) || 0,
        binarySidecars: this.streamingController._binarySidecars || 0,
        binarySidecarFallbacks: this.streamingController._binarySidecarFallbacks || 0,
        maxTrianglesPerFrame: this.streamingController.maxTrianglesPerFrame,
        dynamicMinPx: +(this.streamingController.dynamicMinPx || 0).toFixed(2),
        connectorPxBoost: this.streamingController.connectorPxBoost || 0,
        // WHAT THE BUDGET ADMITS TO DRAW: the floor's fill plus the cone's
        // admitted rows. Since round 9 the cone's share is reserved INSIDE
        // the caps (_coneReserve), so this total is what must not exceed
        // maxTrianglesPerFrame / maxDrawCalls - and what the GPU numbers
        // should be compared with. floorTris/floorCalls are the floor's
        // part alone (what budgetedTris/Calls meant before round 9).
        budgetedTris: (this.streamingController._budgetTris || 0) + ((this.streamingController._coneStats && this.streamingController._coneStats.triangles) || 0),
        maxDrawCalls: this.streamingController.maxDrawCalls,
        budgetedCalls: (this.streamingController._budgetCalls || 0) + ((this.streamingController._coneStats && this.streamingController._coneStats.calls) || 0),
        floorTris: this.streamingController._budgetTris,
        floorCalls: this.streamingController._budgetCalls,
        // Round 11: what the floor held back for in-view tiles whose sidecars
        // had not landed (estimated, not drawn), the tiles counted that way,
        // and the retires the dwell postponed.
        anticipatedTris: Math.round(this.streamingController._anticipatedSpent || 0),
        anticipatedTiles: this.streamingController._anticipatedTiles || 0,
        dwellHolds: this.streamingController._dwellHolds || 0,
        coneReserve: typeof this.streamingController._coneReserve === 'function' ? this.streamingController._coneReserve() : 0,
        libraryBytesMB: this._cache ? Math.round((this._cache.libraryBytes || 0) / 1048576) : undefined,
        maxLibraryBytesMB: Math.round(this.streamingController.maxLibraryBytes / 1048576),
        minFragmentPixels: this.streamingController.minFragmentPixels,
        heavyFragmentPixels: this.streamingController.heavyFragmentPixels,
        batching: this.streamingController.batching,
        batchMaxInstances: this.streamingController.batchMaxInstances,
        // Groups of up to batchFoldMaxInstances rows folded into the tile
        // batch instead of a pool (round 9), and revives that grew a tile's
        // batch instead of adding one.
        batchFoldMaxInstances: this.streamingController.batchFoldMaxInstances,
        batchFoldMaxTris: this.streamingController.batchFoldMaxTris,
        foldedGroups: this.streamingController._foldedGroups || 0,
        batchGrows: this.streamingController._batchGrows || 0,
        // Revives whose rows were appended to the tile batch in place
        // (no rebuild, no copy of the existing items).
        batchAppends: this.streamingController._batchAppends || 0,
        batchedTiles: this.streamingController._batchedTiles || 0,
        batchedItems: this.streamingController._batchedItems || 0,
        singletonMerge: this.streamingController.singletonMerge,
        mergedTiles: this.streamingController._mergedTiles || 0,
        mergedItems: this.streamingController._mergedItems || 0,
        mergeFailures: this.streamingController._mergeFailures || 0,
        // Materialisations whose tile was unloaded or superseded while
        // they were in flight. A climbing number with progress stalled is
        // the signature of a tile being stranded rather than merely slow.
        staleMaterializations: this.streamingController._staleMaterializations || 0,
        mergesRejected: this.streamingController._mergesRejected || 0,
        // Merges with no per-element bounds. Those cannot be culled, so
        // they draw everything they hold - a number worth seeing beside
        // the drawn count rather than inferring from it.
        mergeBoundsMissing: this.streamingController._mergeBoundsMissing || 0,
        // What the merges are holding, and how many tiles took the
        // fragment path because the budget was full.
        mergeBytesMB: Math.round((this.streamingController._mergeBytes || 0) / 1048576),
        maxMergeBytesMB: Math.round((this.streamingController.maxMergeBytes || 0) / 1048576),
        mergeBudgetSkips: this.streamingController._mergeBudgetSkips || 0,
        mergeKept: this.streamingController._mergeKept || 0,
        mergeSortedSeen: this.streamingController._mergeSortedSeen || 0,
        mergeLastError: this.streamingController._mergeLastError || '',
        mergeStaleDropped: this.streamingController._mergeStaleDropped || 0,
        mergeClaimed: this.streamingController._mergeClaimed || 0,
        loadErrors: this.streamingController._loadErrors || 0,
        lastLoadError: this.streamingController._lastLoadError || '',
        // How much of each merge is actually submitted. With the
        // exporter's ordering this is a prefix of the index buffer;
        // without it, everything the merge holds.
        mergePrefixItems: (() => {
          let submitted = 0;
          let sorted = 0;
          try {
            this.streamingController.registry.forEach((tile) => {
              if (!tile._merge) return;
              if (tile._merge.sortedByRadius) sorted++;
              submitted += typeof tile._merge.submittedItems === 'number'
                ? tile._merge.submittedItems
                : (typeof tile._merge.drawnPrefix === 'number'
                  ? tile._merge.drawnPrefix : tile._merge.itemCount);
            });
          } catch (e) { return undefined; }
          return { submitted, sortedMerges: sorted };
        })(),
        mergeMinDrawnFraction: this.streamingController.mergeMinDrawnFraction,
        // How many merged elements actually pass the pixel floor right
        // now, against how many the merges hold. Without the floor these
        // two are the same number and the triangle count says so.
        mergeDrawnItems: (() => {
          let drawn = 0;
          try {
            this.streamingController.registry.forEach((tile) => {
              if (tile._merge && typeof tile._merge.drawnItems === 'number') drawn += tile._merge.drawnItems;
            });
          } catch (e) { return undefined; }
          return drawn;
        })(),
      } : null,
      geometryCache: (() => { const c = this._cache || this.geometryCache; if (!c) return undefined; try { const out = {}; for (const k of Object.keys(c)) if (c[k] instanceof Map) out[k] = c[k].size; return out; } catch (e) { return undefined; } })(),
      heapMB: mem ? { used: Math.round(mem.usedJSHeapSize / 1048576), total: Math.round(mem.totalJSHeapSize / 1048576) } : null,
      camera: (() => {
        try {
          const cc = v.cameraControls;
          const pos = new Vector3(); const tgt = new Vector3();
          if (cc && typeof cc.getPosition === 'function') { cc.getPosition(pos); cc.getTarget(tgt); }
          else if (v.camera) { pos.copy(v.camera.position); }
          // Model box alongside the pose so a pose at the origin can be
          // told from "never framed": a camera at (0,0,0) inside a model
          // centred on the origin is a headless run without auto-frame.
          const b = this.modelProps && this.modelProps.accumulatedBounds;
          const bc = new Vector3(); const bs = new Vector3();
          if (b && !b.isEmpty()) { b.getCenter(bc); b.getSize(bs); }
          return {
            x: +pos.x.toFixed(1), y: +pos.y.toFixed(1), z: +pos.z.toFixed(1),
            tx: +tgt.x.toFixed(1), ty: +tgt.y.toFixed(1), tz: +tgt.z.toFixed(1),
            rawCameraPos: v.camera ? [+v.camera.position.x.toFixed(1), +v.camera.position.y.toFixed(1), +v.camera.position.z.toFixed(1)] : null,
            far: v.camera ? v.camera.far : null, fov: v.camera ? v.camera.fov : null,
            modelCenter: [+bc.x.toFixed(1), +bc.y.toFixed(1), +bc.z.toFixed(1)], modelSize: [+bs.x.toFixed(1), +bs.y.toFixed(1), +bs.z.toFixed(1)],
            framedOnce: !!this.framedOnce, userMovedCamera: !!this.userMovedCamera,
          };
        } catch (e) { return null; }
      })(),
    };
  }

  constructor(viewer, glbs, ReactAPI, isIfcProject) {
    this.viewer = viewer;
    this.urlsModel = glbs;
    this.ReactAPI = ReactAPI;
    this.isIfcProject = isIfcProject;

    this.modelProps = {
      scale: new Vector3(1, 1, 1),
      position: new Vector3(0, 0, 0),
      rotation: new Euler(),
      mapMeshes: new Map(),
      meshes: [],
      created: false,
      accumulatedBounds: new Box3(),
    };

    const useStreaming = !!(glbs?.manifestUrl || (glbs?.manifest && glbs?.baseUrl));
    if (!useStreaming) {
      this._fallback = new LegacyLoader(viewer, glbs, ReactAPI, isIfcProject);
      return;
    }

    if (typeof window !== 'undefined') {
      window.__sl = this;
    }
    // ALSO HUNG ON THE VIEWER, not only on window. A page with two viewers
    // has one window.__sl between them, so a control that reaches for the
    // global would drive whichever loaded last. The clearance toggle asks
    // its own viewer.
    try { viewer.streamingLoader = this; } catch (e) { /* frozen viewer, fall back to the global */ }

    this._initStreaming(glbs).catch(err => {
      console.error('[StreamingLoader] init failed:', err);
      this.ReactAPI.setError(true);
    });
  }

  async _initStreaming(glbs) {
    this.ReactAPI.setPercentsLoaded(5);

    let baseUrl = glbs.baseUrl;
    if (!baseUrl && glbs.manifestUrl) {
      baseUrl = glbs.manifestUrl.replace(/\/manifest\.json\.gz(\?.*)?$/i, '');
    }
    if (!baseUrl && glbs.manifest?.baseUrl) {
      baseUrl = glbs.manifest.baseUrl;
    }
    if (!baseUrl) {
      throw new Error(
        'StreamingLoader: baseUrl missing — provide glbs.baseUrl, ' +
        'glbs.manifestUrl, or a manifest with baseUrl baked in.'
      );
    }

    // Persistent IDB cache for raw fragment GLB bytes + manifest/sidecar
    // JSON — reload-of-same-project hits local storage instead of R2.
    // Declared here (before ManifestLoader) so the same instance is
    // shared by manifest, sidecars, and fragments. Constructor is sync
    // but open() is lazy; if IDB is unavailable (CefSharp older flavors,
    // private mode), the cache flips to no-op and streaming degrades to
    // "always fetch from network" — functionally correct, just no
    // reload speedup. Single instance per session is fine; fragments
    // are global so the cache key is project-agnostic.
    const blobCache = new IDBBlobCache();
    this._blobCache = blobCache;
    // The request count is per model load (perfSnapshot().fetches).
    fetchLedger.reset();
    resetStreamFlags();   // the localStorage switches are read once per load
    // Evictions, reloads and tile churn are per model load too.
    thrashLedger.reset();
    // `?cache=clear` on the address: empty this browser's streaming cache
    // before the manifest is read, so bytes rewritten under an unchanged
    // exportVersion are fetched again (cacheFlags.js).
    if (wantsCacheClear()) await clearStreamingCache(blobCache);

    const sidecarBaseUrl = baseUrl.replace(/\/+$/, '');
    const manifestLoader = new ManifestLoader({
      manifestUrl: glbs.manifestUrl,
      sidecarUrlBuilder: glbs.sidecarUrlBuilder ??
        ((tileId, relativeUrl) => `${sidecarBaseUrl}/${applyTilesBase(relativeUrl, this._tilesBase)}.gz`),
      blobCache,
    });

    this._manifestLoader = manifestLoader;
    const manifest = glbs.manifest ?? await manifestLoader.loadManifest();
    if (this._disposed) return;
    // Direct-to-models exports keep their tiles under the version prefix.
    this._tilesBase = manifest && manifest.tilesBase ? String(manifest.tilesBase) : null;

    // Project "Model files" filter (gear on the project card): learn
    // which source documents are unticked before any sidecar hydrates.
    // Best-effort - a failed lookup shows the whole model.
    // FALLING BACK IS FINE; FALLING BACK IN SILENCE IS NOT.
    //
    // Both of these catches degrade to "show everything", which is the
    // right behaviour - a viewer that draws nothing because a settings
    // lookup failed is worse than one that draws too much. But they said
    // NOTHING, so a page that quietly showed every file the owner had
    // switched off looked exactly like a page that had no files switched
    // off, and nobody could tell which they were looking at. That cost a
    // real investigation on 2026-09-20 (SpaceX BA03: thirteen files
    // hidden, and the question "is this page applying that?" had no
    // answer available from the page).
    //
    // It also records the outcome on the loader so a browser check can
    // read it, because "did the filter activate here" is not answerable
    // from a console message after the fact.
    this._filterState = { sourceFilter: 'pending', placement: 'pending' };
    try {
      await activateSourceFilter(projectIdFromManifestUrl(glbs.manifestUrl), manifest);
      if (this._disposed) return;
      this._filterState.sourceFilter = 'active';
    } catch (e) {
      this._filterState.sourceFilter = `FAILED: ${e && e.message ? e.message : e}`;
      console.warn('[streaming] model-files filter could not be applied - EVERY source file '
        + 'will be drawn, including any switched off on the project card:', e && e.message);
    }
    // Placement overrides (the project card's placement dialog): per
    // file, a translation / rotation / scale / visibility, folded into
    // each row's matrix as sidecars hydrate. Must be active BEFORE the
    // tile registry is built, because tile boxes grow to hold the
    // moved geometry. Best-effort - a failed lookup shows the export
    // as published.
    try {
      await activateDocumentTransforms(projectIdFromManifestUrl(glbs.manifestUrl), manifest);
      if (this._disposed) return;
      this._filterState.placement = 'active';
    } catch (e) {
      this._filterState.placement = `FAILED: ${e && e.message ? e.message : e}`;
      console.warn('[streaming] placement overrides could not be applied - files will be drawn '
        + 'AS PUBLISHED, so a moved file sits where it was exported and a file hidden by the '
        + 'placement dialog is drawn:', e && e.message);
    }

    // Exporter's per-fragment facts (radius / triangle counts / heavy),
    // fetched like a sidecar (gzipped JSON under tiles/). Optional.
    this.fragmentsIndex = null;
    if (manifest.fragmentsIndex) {
      try {
        const fi = await manifestLoader.loadSidecar('__fragmentsIndex', String(manifest.fragmentsIndex).replace(/\.gz$/i, ''));
        if (fi && fi.fragments) this.fragmentsIndex = fi;
      } catch (e) {
        console.warn('[streaming] fragments index unavailable', e && e.message);
      }
    }
    if (this._disposed) return;
    this.heavyFragments = Array.isArray(manifest.heavyFragments) ? manifest.heavyFragments : [];

    this.ReactAPI.setPercentsLoaded(15);

    // ---- Scene root rotation ----
    //
    // Non-IFC: C# pipeline writes everything in three.js Y-up native.
    // No rotation needed; root stays at identity. The Group still
    // exists as a parent for cleanup and to satisfy the
    // viewer.loadedModels contract.
    //
    // IFC (legacy): historical pipeline writes content that needs
    // a (π, π, π) flip to land in viewer frame. Untouched.
    const root = new Group();
    if (this.isIfcProject) {
      root.rotation.set(Math.PI, Math.PI, Math.PI);
      this.modelProps.rotation.set(Math.PI, Math.PI, Math.PI);
    }
    // else: identity rotation — no .rotation.* assignment needed.
    root.updateMatrix();
    root.updateMatrixWorld(true);

    this.viewer.scene.add(root);
    this.viewer.loadedModels.push(root);
    this.modelProps.created = true;

    // Force a scene-level walk so root.matrixWorld is computed
    // (matters for IFC; for non-IFC it's identity either way).
    this.viewer.scene.updateMatrixWorld(true);

    // Snapshot rootMatrix as a value, not a reference. For non-IFC
    // this is the identity matrix; downstream applyMatrix4 calls
    // become no-ops. For IFC it carries the (π, π, π) transform.
    const rootMatrixSnapshot = root.matrixWorld.clone();

    // ---- Convert manifest bounds to viewer frame ----
    //
    // For non-IFC, manifest bounds are already in viewer frame, so
    // applyMatrix4(identity) is a no-op. For IFC, this rotates the
    // bounds through the (π, π, π) flip the same as before.
    let trimmedBoundsViewer = null;
    if (manifest.scene?.trimmedBounds) {
      const tb = manifest.scene.trimmedBounds;
      trimmedBoundsViewer = new Box3(
        new Vector3(...tb.min),
        new Vector3(...tb.max)
      );
      trimmedBoundsViewer.applyMatrix4(rootMatrixSnapshot);
    }

    const cache = new GeometryCache();

    // Worker pool offloads GLTFLoader.parse from the main thread.
    // With ~6,000 unique hashes in real BIM models, parse work
    // previously pegged the main thread and competed with render —
    // user could see this as "the last 6% only loads when I look
    // away". Worker pool keeps parse off the render-thread critical
    // path. Falls back to main-thread parse if construction or any
    // individual parse fails. Size auto-scales by hardwareConcurrency.
    const workerPool = new GLTFWorkerPool({ workerUrl: '/gltfParseWorker.js' });
    this._workerPool = workerPool;
    console.log(`[StreamingLoader] worker pool: available=${workerPool.available} size=${workerPool.size}/${workerPool.maxSize}`);

    // exportVersion is the cache-bust token ManifestLoader parsed
    // from manifest.projectName. Threaded into GLBFetcher so every
    // fragment URL automatically picks up `?v=<token>` and IDB/HTTP
    // caches miss on a fresh export. See ManifestLoader.loadManifest.
    const fetcher = new GLBFetcher({
      baseUrl, cache, blobCache, workerPool,
      exportVersion: manifestLoader.exportVersion || null,
    });
    this._glbFetcher = fetcher;
    // ONE FILE PER LEVEL OF DETAIL, when the export carries it: the
    // fetcher then downloads the level a group will draw rather than the
    // whole fragment (manifest `fragmentLodFiles`). An export without the
    // flag, or a level file that is missing, falls back to the combined
    // file - see GLBFetcher.setLodFiles.
    if (typeof fetcher.setLodFiles === 'function') {
      fetcher.setLodFiles(!!manifest.fragmentLodFiles, manifest.fragmentLodSuffixes);
    }
    // Straight from models.bidlight.com once the export is in that bucket
    // (manifest `modelsBucket`); /stream otherwise. See streamBase.
    if (typeof fetcher.setModelsHost === 'function') {
      fetcher.setModelsHost(!!manifest.modelsBucket);
      if (typeof fetcher.setTilesBase === 'function') fetcher.setTilesBase(this._tilesBase);
    }
    // Fragment packs when the export has them (manifest `fragmentPacks`):
    // the index is loaded before the first tile asks for anything, and a
    // failure simply leaves the one-GLB-per-fragment path on.
    if (manifest.fragmentPacks && packsEnabled() && typeof fetcher.setPacks === 'function') {
      await fetcher.setPacks(manifest.fragmentPacks);
      if (this._disposed) return;
    }
    this._cache = cache;
    this._fetcher = fetcher;
    // THE BANDS, AND WHY THEY ARE NOT THE EXPORT'S. See lodBands.js: it
    // explains the two faults in the rule this replaced (a fine band that
    // grew with the site, and a medium band so wide the coarse tier could
    // never be reached) and it carries the per-browser ?lod= override so a
    // before-and-after is two reloads rather than two releases.
    //
    // The exporter's own lods[] are the fallback when the scene has no
    // bounds; a null here means "use them".
    const lodBands = computeLodBands(manifest);
    const lodSelector = new LODSelector(manifest.lods, lodBands);
    const frustumTester = new FrustumTester(this.viewer.camera);

    const registry = new TileRegistry({
      manifest,
      trimmedBounds: trimmedBoundsViewer,
      rootMatrix: rootMatrixSnapshot,
    });

    // ---- Tile box rotation ----
    //
    // For non-IFC this is a no-op (rotating by identity returns the
    // same box). For IFC, this is what brings tile bboxes into
    // viewer frame so frustum tests succeed. Cheap either way and
    // we keep the code uniform across project types.
    if (!registry._rootMatrixApplied) {
      let rotated = 0;
      registry.forEach(tile => {
        if (tile && tile.box) {
          tile.box.applyMatrix4(rootMatrixSnapshot);
          rotated++;
        }
      });
      registry._rootMatrixApplied = true;
      console.log(
        `[StreamingLoader] processed ${rotated} tile boxes ` +
        `(${this.isIfcProject ? 'IFC π-flip applied' : 'non-IFC identity'})`
      );
    }

    // ---- STATE STORE WIRING ----
    const store = new ViewerStateStore();
    const applicator = new InstanceStateApplicator({ store });
    this._store = store;
    this._applicator = applicator;

    const adapter = new MapMeshesAdapter(this.viewer, { applicator, store });

    // === SHARED PER-HASH INSTANCED-MESH POOLS ===
    //
    // The big draw-call reduction lives here. The registry hands out
    // one GlobalInstancePool per fragment hash; every tile that uses
    // that hash claims slots in the SAME InstancedMesh instead of
    // creating its own. Draw calls drop from O(tiles × hashes_per_tile)
    // to O(unique hashes in scene).
    //
    // Two callbacks:
    //   onPoolCreated → push the new mesh into viewer.meshes (the
    //     raycast set) exactly once.
    //   onMeshSwapped → if a pool grows (doubles its slot capacity), it
    //     swaps to a new InstancedMesh. Replace the entry in
    //     viewer.meshes in-place so raycast doesn't hit the orphaned
    //     old mesh.
    const poolRegistry = new InstancePoolRegistry({
      sceneRoot: root,
      // Letting the registry see the tile registry lets it pre-size
      // each pool on creation from the total instance count across
      // every tile whose sidecar has loaded. With parallel sidecar
      // prefetch firing right after this constructor, most sidecars
      // are in by the time the first pool is created, so pre-sizing
      // is accurate within a small percent and grow becomes rare.
      tileRegistry: registry,
      onPoolCreated: (pool) => {
        if (Array.isArray(this.viewer.meshes)) {
          this.viewer.meshes.push(pool.mesh);
        }
        // Marked at creation so the composite's prepass can leave clearance
        // out without knowing anything about fragments. A pool is one hash,
        // so this is exact rather than a guess about mixed content.
        if (pool.mesh && isClearanceHash(pool.hash)) pool.mesh.userData.isClearance = true;
      },
      // Pool grow swaps the underlying InstancedMesh. Three places hold
      // direct references that go stale: viewer.meshes (raycast set),
      // viewer.meshesObject (per-uid record lookup), and the adapter's
      // activeByTile / activeUidOwner structures. Patch all three here.
      // Without these patches, every selection / hide / show / raycast
      // for tiles materialized BEFORE the grow silently no-ops.
      onPoolDisposed: (_pool, mesh) => {
        if (Array.isArray(this.viewer.meshes)) {
          const i = this.viewer.meshes.indexOf(mesh);
          if (i >= 0) this.viewer.meshes.splice(i, 1);
        }
      },
      onMeshSwapped: (oldMesh, newMesh) => {
        // 1. Raycast set.
        if (Array.isArray(this.viewer.meshes)) {
          const i = this.viewer.meshes.indexOf(oldMesh);
          if (i >= 0) this.viewer.meshes[i] = newMesh;
          else this.viewer.meshes.push(newMesh);
        }
        // 2+3. The per-uid records in meshesObject, and the adapter's
        //    per-tile slot tracking. Every record in meshesObject is put
        //    there by the adapter (register / _promoteSuccessor - the only
        //    two writers) and is an owner record it indexes, so the
        //    adapter rewires them too. This used to walk all of
        //    meshesObject on EVERY pool grow: 3.2 s of a BA03 load on a
        //    real GPU (2026-10-13), for grows that touch one pool.
        adapter._refreshMeshRef(oldMesh, newMesh);
      },
    });
    this._poolRegistry = poolRegistry;

    const outlierVisualizer = new OutlierVisualizer({
      registry,
      sceneRoot: this.viewer.scene,
    });
    this.outlierVisualizer = outlierVisualizer;

    this.streamingController = new StreamingController({
      viewer: this.viewer,
      fragmentsIndex: this.fragmentsIndex,
      heavyFragments: this.heavyFragments,
      registry,
      frustumTester,
      lodSelector,
      fetcher,
      manifestLoader,
      cache,
      adapter,
      applicator,
      sceneRoot: root,
      sidecarPrefetchRadius: null,
      poolRegistry,
      // Per-tile batching. Off unless this BROWSER asks for it - see
      // packingFlags.js: `?packing=batching` for one person on
      // production, or `window.BIDLIGHT_BATCHING = true` for a capture.
      batching: packingEnabled('batching'),
      // Only when the export actually shipped merges AND this browser
      // asks for them, so the two render changes can be measured apart.
      // A merged mesh bakes each element's placement into the geometry,
      // which a placement override cannot move - so merges are off while
      // any override is active and those tiles take the fragment path.
      singletonMerge: manifest.singletonMerge === 1 && packingEnabled('merge') && !documentTransformsActive(),
    });

    this._applicatorUnsub = applicator.bindToStore(adapter);

    // An isolation is an instruction about what to LOAD as well as what
    // to show, so the controller has to hear about it. Without this the
    // model went on streaming into the hidden state behind an isolated
    // view.
    this._isolationUnsub = store.subscribe(({ type }) => {
      if (type === 'isolate' || type === 'clear-isolate') {
        this.streamingController.onIsolationChanged();
      }
    });

    this.modelProps.mapMeshes = this.viewer.meshesObject;
    this.modelProps.meshes = this.viewer.meshes;

    // ---- accumulatedBounds for fit-to-view (viewer frame) ----
    //
    // Prefer fullBounds whenever the manifest has outlier tiles with
    // any instance content — without that, the camera fits to
    // trimmedBounds (the main cluster), the outlier tiles render
    // off-screen, and the user sees "missing geometry" until they
    // manually zoom out. With outliers in the fit, the initial view
    // shows everything; the user can still zoom in for detail.
    //
    // When there are no outliers (or the manifest lacks fullBounds),
    // fall back to the prior trimmedBounds-only logic so the camera
    // doesn't pan way out for a tiny scene that has no far-away
    // content.
    const hasOutlierInstances = (manifest.tiles || []).some(
      t => t.outlier && (t.instanceCount || 0) > 0
    );

    // Outliers millions of times beyond the main cluster are strays
    // (strayBounds.js): they get neither the camera nor the sky.
    const strayOutliers = fullBoundsAreStray(manifest.scene);
    if (strayOutliers && trimmedBoundsViewer) {
      console.warn('[DIAG-EXPORT] fit-to-bounds',
        JSON.stringify({ choice: 'trimmedBounds (outliers too far away to frame)', full: manifest.scene.fullBounds }));
    }

    if (hasOutlierInstances && manifest.scene?.fullBounds && !(strayOutliers && trimmedBoundsViewer)) {
      const fb = manifest.scene.fullBounds;
      const fbBox = new Box3(
        new Vector3(...fb.min),
        new Vector3(...fb.max)
      );
      // No-op for non-IFC, π-flip for IFC.
      fbBox.applyMatrix4(rootMatrixSnapshot);
      this.modelProps.accumulatedBounds.copy(fbBox);
      console.warn('[DIAG-EXPORT] fit-to-bounds',
        JSON.stringify({ choice: 'fullBounds (outliers present)', box: fb }));
    } else if (trimmedBoundsViewer) {
      this.modelProps.accumulatedBounds.copy(trimmedBoundsViewer);
      console.warn('[DIAG-EXPORT] fit-to-bounds',
        JSON.stringify({ choice: 'trimmedBounds (no outliers)' }));
    } else if (manifest.scene?.fullBounds) {
      const fb = manifest.scene.fullBounds;
      const fbBox = new Box3(
        new Vector3(...fb.min),
        new Vector3(...fb.max)
      );
      fbBox.applyMatrix4(rootMatrixSnapshot);
      this.modelProps.accumulatedBounds.copy(fbBox);
    } else {
      registry.forEach(t => {
        if (!t.isOutlier && t.inTrimmedBounds) {
          this.modelProps.accumulatedBounds.union(t.box);
        }
      });
    }

    // Size the sky dome and fog to the model. viewer.setLights builds a
    // 1000-unit placeholder dome "replaced when the model loads", but
    // nothing ever called updateLightingForModel - so a site the size of
    // SpaceX BA03 (2,800 ft across, its terrain link ~10,000 ft) rendered
    // outside the sky. fullBounds is used when the manifest has it:
    // terrain and far links live outside trimmedBounds. Small models
    // keep the placeholder (radius 1000 covers them and the fog stays
    // as it was).
    try {
      let skyBox = this.modelProps.accumulatedBounds;
      if (manifest.scene?.fullBounds?.min && manifest.scene?.fullBounds?.max && !strayOutliers) {
        skyBox = new Box3(
          new Vector3(...manifest.scene.fullBounds.min),
          new Vector3(...manifest.scene.fullBounds.max)
        ).applyMatrix4(rootMatrixSnapshot);
      }
      if (skyBox && !skyBox.isEmpty() && typeof this.viewer.updateLightingForModel === 'function') {
        const size = new Vector3(); const center = new Vector3();
        skyBox.getSize(size); skyBox.getCenter(center);
        if (Math.max(size.x, size.y, size.z) * 10 > 1000) {
          this.viewer.updateLightingForModel({ size, center, bounds: skyBox });
        }
      }
    } catch (e) {
      console.warn('[streaming] sky dome resize failed', e);
    }


    if ('fitToViewBox' in this.viewer) {
      this.viewer.fitToViewBox = this.modelProps.accumulatedBounds;
    }
    if ('boundsForFit' in this.viewer) {
      this.viewer.boundsForFit = this.modelProps.accumulatedBounds;
    }
    // Always published (the two above are gated on properties the viewer
    // never declares, so they never run). The model's bounds in viewer
    // space, from the manifest, so anything that must sit at the model —
    // the ground grid — can find it before a single tile has loaded and
    // wherever the export's coordinate frame put it.
    this.viewer.modelBounds = this.modelProps.accumulatedBounds;

    // ...and put the camera on them, before a single tile has loaded.
    // Until now nothing framed the model on load: the camera sat at its
    // constructed (0,0,0) with the controls' target on the same point,
    // which is degenerate - a drag rotates about zero distance and
    // nothing moves, so the viewer looked unresponsive until a wheel
    // dolly separated them. frameModelOnce only acts when nobody else
    // has placed the camera.
    try { if (typeof this.viewer.frameModelOnce === 'function') this.viewer.frameModelOnce(); } catch (e) { /* a view someone else placed */ }

    // ---- Public API surface on viewer.streaming ----
    this.viewer.streaming = {
      // Live controller handle for cheap per-frame checks
      // (viewer._isStreamingBusy reads its counters, never stats()).
      controller: this.streamingController,
      // Tile dispatch order: 'near' (default) or 'far' (exterior first).
      //   viewer.streaming.setLoadOrder('far')   ← exterior shell paints first
      //   viewer.streaming.setLoadOrder('near')  ← back to default
      // Takes effect on the next tick (no reload needed).
      setLoadOrder: (order) => this.streamingController.setLoadOrder(order),
      getLoadOrder: () => this.streamingController.getLoadOrder(),

      // Priority-boost hint for the tile dispatch queue. Consumed by
      // the queue sort in StreamingController.tick() — priority tiles
      // float to the front of the (near|far)-distance sort, but the
      // frustum/coverage/hysteresis/LOD gates are still respected
      // (a boosted tile that isn't a load candidate on its own merits
      // won't dispatch). Additive: multiple callers can hint, and
      // clearPrefetch() empties the set.
      //
      // Used by IssueResolutionPage on candidate-fix elements so the
      // tiles carrying auto-detected candidates paint first.
      prefetchTiles: (tileKeys) => this.streamingController.addPriorityTiles(tileKeys),
      clearPrefetch: () => this.streamingController.clearPriorityTiles(),

      // Elements that must be on screen at any distance, whatever the
      // pixel floor would say. Construction tracking pins the elements
      // it has coloured: the point of that picture is to read the state
      // of the job from far enough away to see the whole job, and the
      // small elements are exactly the ones being looked for.
      //   viewer.streaming.setPinnedUids([...])   pin
      //   viewer.streaming.setPinnedUids([])      clear
      setPinnedUids: (uids) => this.streamingController.setPinnedUids(uids),

      // Elements a page is READING THE ROWS OF - the Project Files tree, when
      // a branch is opened - built whatever their size on screen, so the rows
      // stop showing the no-mesh icon for geometry that was only unfetched.
      // Its own set, never pinnedUids: tracking owns that one and sets it
      // wholesale, so sharing would have one cancel the other invisibly.
      // Bounded, and ensuredStatus().truncated says when the bound was hit:
      //   viewer.streaming.ensureUids([...uids], { cap: 20000 })
      //   viewer.streaming.ensuredStatus()  -> { uids, truncated, overflow }
      //   viewer.streaming.clearEnsuredUids()
      ensureUids: (uids, opts) => this.streamingController.ensureUids(uids, opts),
      clearEnsuredUids: () => this.streamingController.clearEnsuredUids(),
      ensuredStatus: () => this.streamingController.ensuredStatus(),
      // Every fragment loads whatever its size on screen: no pixel floor,
      // no triangle budget, no heavy hold-back. Level of detail still
      // follows distance. Construction tracking's plan view asks for it;
      // nothing else does.
      //   viewer.streaming.setDrawEverything(true)
      setDrawEverything: (on) => this.streamingController.setDrawEverything(on),

      // The guarantee on what the camera is pointing at: every tile in
      // view loads every fragment, whatever its size (detail still
      // follows size). focusStatus() says what it covers and what it
      // still owes, so a caller can wait for the view to be complete:
      //   viewer.streaming.focusStatus()      -> { tiles, pending, ... }
      //   viewer.streaming.setFocusGuarantee(false)  back to the floors
      focusStatus: () => this.streamingController.focusStatus(),
      // The cold-start burst window (streamingController `coldStartMs`):
      //   viewer.streaming.coldStartStatus()  -> { active, msRemaining, ... }
      coldStartStatus: () => this.streamingController.coldStartStatus(),
      setFocusGuarantee: (on) => this.streamingController.setFocusGuarantee(on),
      getDrawEverything: () => !!this.streamingController.drawEverything,

      setShowOutliers: (show) => this.streamingController.setShowOutliers(show),
      toggleShowOutliers: () => {
        const next = !this.streamingController.getShowOutliers();
        this.streamingController.setShowOutliers(next);
        return next;
      },
      getShowOutliers: () => this.streamingController.getShowOutliers(),

      setShowOutlierBboxes: (show) => {
        if (show) outlierVisualizer.enable();
        else outlierVisualizer.disable();
      },
      toggleShowOutlierBboxes: () => outlierVisualizer.toggle(),
      getShowOutlierBboxes: () => outlierVisualizer.group.visible,

      // === DISABLED-BY-EXPORTER OVERRIDE — bulk helpers ===
      //
      // GLBs marked `disabled` by the C# pipeline are downloaded and
      // their pool slots are acquired at materialize time, but the
      // applicator renders them zero-scale until the user explicitly
      // reveals them. These two methods walk the pool registry's
      // userData arrays to find every disabled uid currently in the
      // scene and toggle the override en masse — useful for a
      // "show / hide all disabled" toolbar button.
      //
      // Per-uid and per-subset granularity is on store directly:
      //   viewer.streaming.state.showDisabled(uid)
      //   viewer.streaming.state.bulkShowDisabled([...uids])
      //   viewer.streaming.state.hideDisabled(uid)
      //   viewer.streaming.state.bulkHideDisabled([...uids])
      enumerateDisabledUids: () => {
        const out = [];
        // 3-pool split: each Map value is [pool0, pool1, pool2] (sparse).
        // Walk every allocated pool. A disabled uid is the same uid no
        // matter which LOD bucket it currently lives in, so dedup via
        // a set in case the same tile migrated mid-enumeration.
        const seen = new Set();
        poolRegistry.pools.forEach((arr) => {
          if (!Array.isArray(arr)) return;
          for (const pool of arr) {
            if (!pool || !pool.mesh) continue;
            const ud = pool.mesh.userData;
            if (!Array.isArray(ud)) continue;
            for (let i = 0; i < ud.length; i++) {
              const e = ud[i];
              if (e && e.disabled && !seen.has(e.uniqueId)) {
                seen.add(e.uniqueId);
                out.push(e.uniqueId);
              }
            }
          }
        });
        return out;
      },
      showAllDisabled: () => {
        const uids = this.viewer.streaming.enumerateDisabledUids();
        if (uids.length > 0) store.bulkShowDisabled(uids);
        return uids.length;
      },
      hideAllDisabled: () => {
        const uids = this.viewer.streaming.enumerateDisabledUids();
        if (uids.length > 0) store.bulkHideDisabled(uids);
        return uids.length;
      },

      // State store API surface — viewer's hide/isolate/select/highlight
      // call sites route through these instead of direct instanceMatrix
      // manipulation.
      state: store,
      applicator,

      // Every uniqueId currently materialized in the scene (one entry
      // per active slot). The store's ghost/highlight buckets are
      // keyed by uid and have to be told WHICH uids to ghost, so a
      // caller that wants "everything except these" - the issue page's
      // 3D change review, which drops the model back and leaves the
      // changed elements in colour - needs the live set. Recomputed on
      // call rather than cached: tiles stream in and out constantly.
      enumerateActiveUids: () => [...adapter.activeUidOwner.keys()],
      adapter,

      stats: () => ({
        cache: this._cache.stats(),
        fetcher: this._fetcher.stats(),
        controller: this.streamingController.stats(),
        adapter: adapter.stats(),
        store: store.stats(),
        blobCache: this._blobCache.stats(),
        outlierBboxesVisible: outlierVisualizer.group.visible,
      }),
    };

    this.ReactAPI.setPercentsLoaded(50);

    // === PARALLEL SIDECAR PREFETCH ===
    //
    // Kick off every in-bounds tile's sidecar fetch in one shot,
    // BEFORE the tick loop starts dispatching materialize work. This
    // is the single biggest first-load latency win:
    //
    // Without it, the tick loop serializes "sidecar then fragments"
    // per-tile inside each concurrent _beginLoad slot. With 32 slots
    // and ~850 tiles, that's ~27 batches × sidecar RTT just to learn
    // which fragments the tiles need. Sidecars are tiny (a few KB),
    // so paying full RTT for each is pure waste — they'd multiplex
    // happily over a single HTTP/2 connection.
    //
    // ManifestLoader.loadSidecar dedupes by URL via its in-flight map,
    // so the tick loop's later _loadSidecar calls join the in-flight
    // promise we kick off here — no double-fetch.
    //
    // Fire-and-forget on purpose. We DON'T await — the tick loop
    // immediately starts dispatching materialize work for tiles whose
    // sidecars have already landed, and queued tiles whose sidecars
    // are still in flight join the in-flight promise when their turn
    // comes. End result: sidecar overhead overlaps with materialize
    // work, not serialized in front of it.
    const sidecarsKicked = this.streamingController.prefetchAllSidecars();
    if (sidecarsKicked > 0) {
      console.log(`[StreamingLoader] kicked off ${sidecarsKicked} sidecar prefetches`);
    }

    // === BACKGROUND STREAMING TICK ===
    //
    // Run the streaming controller's tick on a setInterval-driven
    // background loop instead of inside the render's rAF.
    //
    // Why: the previous monkey-patch of viewer.animate ran the tick
    // BEFORE every frame. When rAF stalls — modal dialogs grabbing
    // focus, the GPU blocking on a heavy materialize, the user
    // dragging a non-3D UI element, frame budget eaten by something
    // unrelated — dispatch stalls with it. Worse, on browser tabs
    // that aren't currently visible rAF drops to ~1 Hz, which means
    // a project the user clicked in and then switched away from
    // basically stops downloading.
    //
    // setInterval at 16 ms decouples streaming from render: a tab
    // can be visually idle (no rAF firing because nothing changed,
    // or render budget is consumed by other work) and tiles keep
    // populating. In-flight fetches were ALWAYS independent of rAF
    // because `fetch` and `await` don't care about render frames —
    // but we needed the dispatch step itself to be independent too,
    // since that's where new fetches get kicked off as concurrency
    // slots free up.
    //
    // Caveat: setInterval also throttles to ~1 Hz when the tab is
    // truly hidden (a documented browser behavior since Chrome 56).
    // Fully background streaming for hidden tabs would need a Web
    // Worker — separate, multi-week project. For tabs that are open
    // even if not currently focused, this delivers the "back thread"
    // behavior you asked for: tick keeps firing.
    //
    // The animate-loop monkey-patch is gone. The viewer's normal
    // animate() runs untouched — only render work, no streaming
    // mixed in. tick continues to drive LOD-on-camera-rest from
    // inside its own loop using its own absolute-time comparison.
    if (this._tickIntervalId) clearInterval(this._tickIntervalId);
    this._tickIntervalId = setInterval(() => {
      try {
        this.streamingController.tick();
      } catch (err) {
        console.error('[StreamingLoader] tick error:', err);
      }
    }, 16);

    this.ReactAPI.setPercentsLoaded(100);
    this.ReactAPI.setLoading(false);
    this.ReactAPI.setAllowToResize(true);

    if (this.viewer.renderer) {
      this.viewer.scene.updateMatrixWorld(true);
      // One place decides how a frame is drawn (viewer.drawFrame).
      if (this.viewer.drawFrame) this.viewer.drawFrame();
      else this.viewer.renderer.render(this.viewer.scene, this.viewer.camera);
    }
    if (this.viewer.animate) this.viewer.animate();

    // [DIAG-EXPORT] one-shot streaming completion summary. Logged
    // ~3s after the initial 100% so the controller's first tick has
    // had time to load visible tiles + apply instances. The deltas
    // between manifest-reported counts (logged earlier by
    // ManifestLoader) and these numbers tell us where geometry got
    // dropped: failed fetches show up under totalFailures, parse
    // errors show in workerFallbacks, and unapplied instances mean
    // the InstanceStateApplicator never received the bucket.
    setTimeout(() => {
      try {
        const fStats = this._fetcher && this._fetcher.stats
          ? this._fetcher.stats()
          : null;
        const cStats = this.streamingController && this.streamingController.stats
          ? this.streamingController.stats()
          : null;
        const mStats = manifestLoader.stats || null;
        let tileCount = 0;
        let tileInstSum = 0;
        if (registry && typeof registry.forEach === 'function') {
          registry.forEach((t) => {
            tileCount++;
            tileInstSum += (t.instanceCount || (t.sidecar?.instances?.length) || 0);
          });
        }
        console.warn(
          '[DIAG-EXPORT] streaming-complete',
          JSON.stringify({
            fetcher: fStats,
            controller: cStats,
            manifestCache: mStats,
            tilesInRegistry: tileCount,
            tileInstanceSum: tileInstSum,
          })
        );
      } catch (e) {
        console.warn('[DIAG-EXPORT] streaming-complete logging failed:', e && e.message);
      }
    }, 3000);
  }

  dispose() {
    // Checked after every await in _initStreaming: a viewer torn down
    // while its manifest was still loading otherwise went on to build a
    // controller and fetch the whole model for nobody.
    this._disposed = true;
    // Part -> parent pairs are keyed by uniqueId and uniqueIds are
    // PER EXPORT. A map left behind would resolve a click in the NEXT
    // model to an element that is not in it - and resolve it silently,
    // because a wrong uid looks exactly like a right one.
    forgetNestedParts();
    // The console handle (window.__sl) would keep the whole disposed
    // session - controller, pools, geometry - reachable.
    try { if (typeof window !== 'undefined' && window.__sl === this) window.__sl = null; } catch (e) { /* best effort */ }
    try { if (this.viewer && this.viewer.streamingLoader === this) this.viewer.streamingLoader = null; } catch (e) { /* best effort */ }
    if (this._fallback) return;
    try { if (this._manifestLoader) this._manifestLoader.close(); } catch (e) { /* best effort */ }
    try { if (this._glbFetcher) this._glbFetcher.close(); } catch (e) { /* best effort */ }
    if (this._tickIntervalId) {
      clearInterval(this._tickIntervalId);
      this._tickIntervalId = null;
    }
    if (this._applicatorUnsub) this._applicatorUnsub();
    if (this._isolationUnsub) { this._isolationUnsub(); this._isolationUnsub = null; }
    this.outlierVisualizer.dispose();
    if (this._workerPool) {
      this._workerPool.dispose();
      this._workerPool = null;
    }
    this.streamingController = null;
  }
}